import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useTabNavigate } from "@/hooks/useTabNavigate";
import { useCustomers, useSoftDeleteCustomer, type Customer } from "@/hooks/useCustomers";
import { usePermissions } from "@/hooks/usePermissions";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { NewCustomerDialog } from "@/components/controller/NewCustomerDialog";
import { CustomerListTable } from "@/components/controller/customers/CustomerListTable";
import { CustomerDetailPanel } from "@/components/controller/customers/CustomerDetailPanel";
import { CustomerMobileCards } from "@/components/controller/customers/CustomerMobileCards";
import { CustomerMergeDialog } from "@/components/controller/customers/CustomerMergeDialog";
import { Search, Plus, FileSpreadsheet, FileText, Printer, GitMerge, ExternalLink, Pencil, Trash2, AlertTriangle } from "lucide-react";
import { useCustomerDuplicates } from "@/hooks/useCustomerDuplicates";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";
import { RequirePermission } from "@/components/RequirePermission";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

// ── Scroll restoration helpers ──────────────────────────────────────────
const SCROLL_KEY = "customers_scroll_state";

interface ScrollState {
  scrollTop: number;
  selectedId: string | null;
  page: number;
  pageSize?: number;
}

function saveScrollState(state: ScrollState) {
  try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify(state)); } catch {}
}

function consumeScrollState(): ScrollState | null {
  try {
    const raw = sessionStorage.getItem(SCROLL_KEY);
    sessionStorage.removeItem(SCROLL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// ── Project counts hook ─────────────────────────────────────────────────
const useCustomerProjectCounts = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["customer_project_counts"],
    queryFn: async () => {
      const { data, error } = await supabase.from("projects").select("customer_id, status");
      if (error) throw error;
      const counts: Record<string, { total: number; active: number }> = {};
      data?.forEach((p) => {
        if (p.customer_id) {
          if (!counts[p.customer_id]) counts[p.customer_id] = { total: 0, active: 0 };
          counts[p.customer_id].total++;
          if (p.status === "active") counts[p.customer_id].active++;
        }
      });
      return counts;
    },
    enabled: !!user,
  });
};

// ── Sync badge constants (used on mobile Sheet) ─────────────────────────
const TYPE_LABELS: Record<string, string> = {
  general_contractor: "General Contractor",
  property_manager: "Property Manager",
  owner: "Owner",
  insurance_company: "Insurance Co.",
  other: "Other",
};

const SYNC_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  synced: { label: "Synced", variant: "default" },
  pending: { label: "Pending", variant: "secondary" },
  error: { label: "Error", variant: "destructive" },
  not_synced: { label: "Not Synced", variant: "outline" },
};

// ═══════════════════════════════════════════════════════════════════════
const CustomersInner = () => {
  const navigate = useTabNavigate();
  const isMobile = useIsMobile();
  const pagination = usePagination(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { data: customers, isLoading, isFetching, isPlaceholderData } = useCustomers(pagination, debouncedSearch);
  const { data: projectCounts } = useCustomerProjectCounts();
  const deleteCustomer = useSoftDeleteCustomer();
  const { can } = usePermissions();

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);

  // Selection state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  // Merge state
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState<Set<string>>(new Set());
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // Duplicate detection
  const { groups: duplicateGroups, duplicateIds, allCustomers: allDuplicateCustomers } = useCustomerDuplicates();
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  // Scroll restoration ref
  const listRef = useRef<HTMLDivElement>(null);
  const restoredRef = useRef(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      pagination.setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => {
    if (!showDuplicatesOnly) return customers ?? [];
    // When showing duplicates, use the full (unpaginated) customer list from the duplicates hook
    return allDuplicateCustomers.filter((c) => duplicateIds.has(c.id));
  }, [customers, showDuplicatesOnly, duplicateIds, allDuplicateCustomers]);

  // ── Scroll restoration on mount ──────────────────────────────────────
  useEffect(() => {
    if (restoredRef.current || !customers?.length) return;
    const saved = consumeScrollState();
    if (!saved) { restoredRef.current = true; return; }

    // Restore page size first if different
    if (saved.pageSize && saved.pageSize !== pagination.pageSize) {
      pagination.setPageSize(saved.pageSize);
      // setPageSize resets to page 1, so we need to restore page after
    }

    if (saved.page && saved.page !== pagination.page) {
      pagination.setPage(saved.page);
      return; // wait for data to reload on correct page
    }

    if (saved.selectedId) {
      const idx = customers.findIndex((c) => c.id === saved.selectedId);
      if (idx >= 0) {
        setSelectedCustomer(customers[idx]);
        setSelectedIndex(idx);
      }
    }

    if (saved.scrollTop && listRef.current) {
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({ top: saved.scrollTop });
      });
    }
    restoredRef.current = true;
  }, [customers, pagination.page]);

  // ── Handlers ──────────────────────────────────────────────────────────
  const handleSelect = useCallback((c: Customer) => {
    setSelectedCustomer(c);
    setSelectedIndex(filtered.findIndex((x) => x.id === c.id));
  }, [filtered]);

  const handleDoubleClick = useCallback((c: Customer) => {
    saveScrollState({
      scrollTop: listRef.current?.scrollTop ?? 0,
      selectedId: c.id,
      page: pagination.page,
      pageSize: pagination.pageSize,
    });
    navigate(`/controller/customers/${c.id}`);
  }, [navigate, pagination.page]);

  const handleDelete = async (c: Customer) => {
    try {
      await deleteCustomer.mutateAsync(c.id);
      toast.success("Customer deleted");
      setSelectedCustomer(null);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // ── Keyboard navigation ───────────────────────────────────────────────
  useEffect(() => {
    if (!selectedCustomer || isMobile || !filtered.length) return;

    const handler = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const newIdx = Math.min(selectedIndex + 1, filtered.length - 1);
        setSelectedIndex(newIdx);
        setSelectedCustomer(filtered[newIdx]);
        document.querySelector(`[data-customer-index="${newIdx}"]`)?.scrollIntoView({ block: "nearest" });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const newIdx = Math.max(selectedIndex - 1, 0);
        setSelectedIndex(newIdx);
        setSelectedCustomer(filtered[newIdx]);
        document.querySelector(`[data-customer-index="${newIdx}"]`)?.scrollIntoView({ block: "nearest" });
      } else if (e.key === "Enter") {
        e.preventDefault();
        handleDoubleClick(selectedCustomer);
      } else if (e.key === "Escape") {
        setSelectedCustomer(null);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [selectedCustomer, selectedIndex, filtered, isMobile, handleDoubleClick]);

  // ── Merge helpers ─────────────────────────────────────────────────────
  const handleMergeToggle = (id: string) => {
    setMergeSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else if (next.size < 2) next.add(id);
      return next;
    });
  };

  const mergeCustomers = (() => {
    if (mergeSelected.size !== 2) return null;
    const ids = Array.from(mergeSelected);
    return [filtered.find((c) => c.id === ids[0])!, filtered.find((c) => c.id === ids[1])!] as [Customer, Customer];
  })();

  const toggleMergeMode = () => {
    setMergeMode((v) => !v);
    setMergeSelected(new Set());
  };

  // ── Export / Page actions ─────────────────────────────────────────────
  const { doExport } = useExportActions({ entityType: "customers" });
  const hasData = filtered.length > 0;

  const pageActions = [
    {
      label: "New Customer",
      icon: Plus,
      onClick: () => { setEditCustomer(null); setDialogOpen(true); },
      permission: can("manage_customers"),
    },
    {
      label: mergeMode ? "Cancel Merge" : "Merge Customers",
      icon: GitMerge,
      onClick: toggleMergeMode,
      permission: can("manage_customers") && hasData,
      separator: true,
    },
    {
      label: "Export to Excel",
      icon: FileSpreadsheet,
      onClick: () => hasData && doExport("excel", filtered),
      permission: hasData,
      separator: !can("manage_customers"),
    },
    {
      label: "Export to CSV",
      icon: FileText,
      onClick: () => hasData && doExport("csv", filtered),
      permission: hasData,
    },
    {
      label: "Print",
      icon: Printer,
      onClick: () => hasData && doExport("print", filtered),
      permission: hasData,
    },
  ];

  const syncBadge = (status: string | null) => {
    const s = SYNC_BADGE[status || "not_synced"] || SYNC_BADGE.not_synced;
    return <Badge variant={s.variant} className="text-[10px]">{s.label}</Badge>;
  };

  // ── Render ────────────────────────────────────────────────────────────
  const listContent = (
    <div className="h-full flex flex-col gap-4 overflow-hidden" ref={listRef}>
      <div className="flex items-center justify-between shrink-0">
        <h1 className="text-2xl font-bold tracking-tight">Customers</h1>
        <PageActions actions={pageActions} />
      </div>

      <div className="relative max-w-sm shrink-0">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search by name, number, email, phone…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>

      {/* Duplicates banner */}
      {duplicateGroups.length > 0 && (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant={showDuplicatesOnly ? "default" : "outline"}
            size="sm"
            className="gap-1.5"
            onClick={() => setShowDuplicatesOnly((v) => !v)}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            {duplicateGroups.length} Duplicate Group{duplicateGroups.length !== 1 ? "s" : ""}
            <Badge variant="secondary" className="ml-1 text-[10px]">
              {duplicateIds.size} records
            </Badge>
          </Button>
          {showDuplicatesOnly && (
            <span className="text-xs text-muted-foreground">
              Showing only potential duplicates. Select 2 and merge to resolve.
            </span>
          )}
        </div>
      )}

      {mergeMode && (
        <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-muted text-sm shrink-0">
          <GitMerge className="h-4 w-4 text-muted-foreground" />
          <span>Select exactly 2 customers to merge.</span>
          {mergeSelected.size === 2 && (
            <Button size="sm" variant="destructive" onClick={() => setMergeDialogOpen(true)}>
              Merge Selected
            </Button>
          )}
        </div>
      )}

      <div className={`flex-1 overflow-y-auto min-h-0 relative ${isPlaceholderData ? "opacity-60 pointer-events-none" : ""} transition-opacity duration-150`}>
        {isLoading && !filtered.length ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : !filtered.length ? (
          <p className="text-muted-foreground">No customers found.</p>
        ) : isMobile ? (
          <CustomerMobileCards customers={filtered} projectCounts={projectCounts} onSelect={handleSelect} />
        ) : (
          <CustomerListTable
            customers={filtered}
            selectedId={selectedCustomer?.id ?? null}
            projectCounts={projectCounts}
            onSelect={handleSelect}
            onDoubleClick={handleDoubleClick}
            onProjectsClick={(c) => {
              saveScrollState({ scrollTop: listRef.current?.scrollTop ?? 0, selectedId: c.id, page: pagination.page, pageSize: pagination.pageSize });
              navigate(`/controller/customers/${c.id}?tab=projects`);
            }}
            mergeMode={mergeMode}
            mergeSelected={mergeSelected}
            onMergeToggle={handleMergeToggle}
            duplicateIds={duplicateIds}
          />
        )}
        {isFetching && filtered.length > 0 && (
          <div className="absolute inset-x-0 top-0 h-0.5 bg-primary/30 animate-pulse" />
        )}
      </div>

      {filtered.length > 0 && !showDuplicatesOnly && (
        <div className="shrink-0">
          <PaginationControls pagination={pagination} />
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Desktop: split-pane layout */}
      {!isMobile ? (
        <div className="h-full overflow-hidden">
          {selectedCustomer ? (
            <ResizablePanelGroup direction="horizontal" className="h-full">
              <ResizablePanel defaultSize={55} minSize={35}>
                {listContent}
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={45} minSize={25}>
                <CustomerDetailPanel
                  customer={selectedCustomer}
                  projectCounts={projectCounts?.[selectedCustomer.id]}
                  canManage={can("manage_customers")}
                  isDeleting={deleteCustomer.isPending}
                  onClose={() => { setSelectedCustomer(null); setSelectedIndex(-1); }}
                  onViewFull={() => handleDoubleClick(selectedCustomer)}
                  onEdit={() => {
                    setEditCustomer(selectedCustomer);
                    setDialogOpen(true);
                  }}
                  onDelete={() => handleDelete(selectedCustomer)}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            listContent
          )}
        </div>
      ) : (
        <>
          {listContent}

          {/* Mobile: Sheet overlay for detail view */}
          <Sheet open={!!selectedCustomer} onOpenChange={(v) => { if (!v) setSelectedCustomer(null); }}>
            <SheetContent className="sm:max-w-md overflow-y-auto">
              {selectedCustomer && (
                <>
                  <SheetHeader>
                    <SheetTitle>
                      {selectedCustomer.customer_number != null && (
                        <span className="font-mono text-muted-foreground mr-2">#{selectedCustomer.customer_number}</span>
                      )}
                      {selectedCustomer.name}
                    </SheetTitle>
                    <SheetDescription>Customer Details</SheetDescription>
                  </SheetHeader>
                  <div className="mt-4 space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div><p className="text-muted-foreground text-xs">Type</p><p>{TYPE_LABELS[selectedCustomer.customer_type || "other"]}</p></div>
                      <div><p className="text-muted-foreground text-xs">Contact</p><p>{selectedCustomer.contact_name || "—"}</p></div>
                      <div><p className="text-muted-foreground text-xs">Email</p><p>{selectedCustomer.email || "—"}</p></div>
                      <div><p className="text-muted-foreground text-xs">Phone</p><p>{selectedCustomer.phone || "—"}</p></div>
                    </div>
                    {(selectedCustomer.billing_address || selectedCustomer.billing_city) && (
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Billing Address</p>
                        <p>{[selectedCustomer.billing_address, selectedCustomer.billing_city, selectedCustomer.billing_state, selectedCustomer.billing_zip].filter(Boolean).join(", ")}</p>
                      </div>
                    )}
                    {(selectedCustomer.job_address || selectedCustomer.job_city) && (
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Job Address</p>
                        <p>{[selectedCustomer.job_address, selectedCustomer.job_city, selectedCustomer.job_state, selectedCustomer.job_zip].filter(Boolean).join(", ")}</p>
                      </div>
                    )}
                    {selectedCustomer.notes && (
                      <div>
                        <p className="text-muted-foreground text-xs mb-1">Notes</p>
                        <p>{selectedCustomer.notes}</p>
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">QuickBooks</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">ID: {selectedCustomer.qb_customer_id || "—"}</span>
                        {syncBadge(selectedCustomer.qb_sync_status)}
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <p className="text-xs text-muted-foreground">Projects: {projectCounts?.[selectedCustomer.id]?.total || 0}</p>
                      <p className="text-xs text-muted-foreground">Created: {format(new Date(selectedCustomer.created_at), "MMM d, yyyy")}</p>
                    </div>
                  </div>
                  <SheetFooter className="mt-6 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setSelectedCustomer(null); navigate(`/controller/customers/${selectedCustomer.id}`); }}>
                      <ExternalLink className="mr-1 h-3 w-3" />Full Details
                    </Button>
                    {can("manage_customers") && (
                      <Button variant="outline" size="sm" onClick={() => { setEditCustomer(selectedCustomer); setDialogOpen(true); }}>
                        <Pencil className="mr-1 h-3 w-3" />Edit
                      </Button>
                    )}
                    {can("manage_customers") && (
                      <Button variant="destructive" size="sm" disabled={deleteCustomer.isPending} onClick={() => handleDelete(selectedCustomer)}>
                        <Trash2 className="mr-1 h-3 w-3" />Delete
                      </Button>
                    )}
                  </SheetFooter>
                </>
              )}
            </SheetContent>
          </Sheet>
        </>
      )}

      {/* New / Edit Dialog */}
      <NewCustomerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editCustomer={editCustomer}
        onCreated={() => {}}
      />

      {/* Merge Dialog */}
      {mergeCustomers && (
        <CustomerMergeDialog
          open={mergeDialogOpen}
          onOpenChange={(v) => {
            setMergeDialogOpen(v);
            if (!v) { setMergeMode(false); setMergeSelected(new Set()); }
          }}
          customerA={mergeCustomers[0]}
          customerB={mergeCustomers[1]}
        />
      )}
    </>
  );
};

const Customers = () => (
  <RequirePermission anyOf={["manage_customers", "view_projects"]}>
    <CustomersInner />
  </RequirePermission>
);

export default Customers;
